import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useConfig } from "../context/config-context";
import axios from "axios";

/**
 * Page d'accès direct à un document via token temporaire (24h)
 * - Utilisé pour les notifications instantanées CR
 * - Token à usage unique
 */
const ReportAccessPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const siteConfig = useConfig();

  const [status, setStatus] = useState("loading"); // loading, valid, invalid, expired, used
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState("");

  const verifyAndLoadToken = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_CREDENTIAL}/report-access/verify/${token}`,
      );

      if (response.data.success) {
        setReportData(response.data.data);
        setStatus("valid");
      } else {
        setStatus("invalid");
        setError(response.data.error?.message || "Lien invalide");
      }
    } catch (err) {
      console.error("Erreur vérification token:", err);
      const errorCode = err.response?.data?.error?.code;
      if (errorCode === "TOKEN_ALREADY_USED") {
        setStatus("used");
      } else if (errorCode === "TOKEN_EXPIRED") {
        setStatus("expired");
      } else {
        setStatus("invalid");
      }
      setError(
        err.response?.data?.error?.message ||
          "Erreur lors de la vérification du token"
      );
    }
  }, [token]);

  useEffect(() => {
    verifyAndLoadToken();
  }, [verifyAndLoadToken]);

  const handleDownload = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_CREDENTIAL}/report-access/download/${token}`,
        {
          responseType: "blob",
        },
      );

      // Si le serveur retourne du JSON (erreur), le content-type ne sera pas application/pdf
      const contentType = response.headers["content-type"] || "";
      if (contentType.includes("application/json")) {
        // Lire le blob comme texte pour extraire l'erreur
        const text = await response.data.text();
        const json = JSON.parse(text);
        const code = json?.error?.code;
        if (code === "TOKEN_ALREADY_USED") {
          setStatus("used");
        } else if (code === "TOKEN_EXPIRED") {
          setStatus("expired");
        } else {
          setStatus("invalid");
          setError(json?.error?.message || "Erreur lors du téléchargement");
        }
        return;
      }

      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", reportData?.documentName || "document.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Marquer comme utilisé
      setStatus("used");
    } catch (err) {
      console.error("Erreur téléchargement:", err);
      // Erreur HTTP (4xx/5xx) - axios lance une exception
      const code = err.response?.data?.error?.code;
      if (code === "TOKEN_ALREADY_USED") {
        setStatus("used");
      } else if (code === "TOKEN_EXPIRED") {
        setStatus("expired");
      } else {
        setError("Erreur lors du téléchargement du document");
      }
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: siteConfig.cssVariables["--color-bg"] }}
    >
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="mb-4">
            <img
              src={siteConfig.medias.logo_isis.path}
              alt={siteConfig.medias.logo_isis.alt}
              className="h-16 mx-auto"
            />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            Accès au document
          </h1>
        </div>

        {/* Loading */}
        {status === "loading" && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Vérification du lien...</p>
          </div>
        )}

        {/* Valid token */}
        {status === "valid" && reportData && (
          <div className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start">
                <svg
                  className="h-5 w-5 text-green-500 mt-0.5 mr-3"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <div>
                  <p className="text-sm font-medium text-green-800">
                    Lien valide
                  </p>
                  <p className="text-sm text-green-700 mt-1">
                    Votre document est prêt à être téléchargé
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <dl className="space-y-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Patient
                  </dt>
                  <dd className="text-sm text-gray-900">
                    {reportData.patientName}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Document
                  </dt>
                  <dd className="text-sm text-gray-900">
                    {reportData.documentName}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Type</dt>
                  <dd className="text-sm text-gray-900">
                    {reportData.documentType}
                  </dd>
                </div>
              </dl>
            </div>

            <button
              onClick={handleDownload}
              className="w-full py-3 px-4 rounded-lg font-medium text-white transition-colors"
              style={{
                backgroundColor: siteConfig.cssVariables["--primary"],
              }}
              onMouseEnter={(e) => {
                e.target.style.opacity = "0.85";
              }}
              onMouseLeave={(e) => {
                e.target.style.opacity = "1";
              }}
            >
              Télécharger le document
            </button>

            <p className="text-xs text-gray-500 text-center">
              Ce lien est à usage unique et expire après 24 heures
            </p>
          </div>
        )}

        {/* Used token */}
        {status === "used" && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
            <svg
              className="h-12 w-12 text-gray-400 mx-auto mb-4"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <p className="text-gray-900 font-medium mb-2">
              Document téléchargé
            </p>
            <p className="text-sm text-gray-600">
              Le document a été téléchargé avec succès. Ce lien ne peut plus
              être utilisé.
            </p>
          </div>
        )}

        {/* Invalid/Expired token */}
        {(status === "invalid" ||
          status === "expired" ||
          status === "TOKEN_EXPIRED" ||
          status === "TOKEN_ALREADY_USED") && (
          <div className="space-y-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <svg
                className="h-12 w-12 text-red-400 mx-auto mb-4"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <p className="text-red-900 font-medium mb-2">Lien invalide</p>
              <p className="text-sm text-red-700">
                {error ||
                  "Ce lien d'accès est invalide, a expiré ou a déjà été utilisé."}
              </p>
            </div>

            <div className="text-center">
              <button
                onClick={() => navigate("/")}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Retour à l'accueil
              </button>
            </div>
          </div>
        )}

        {/* Error */}
        {error && status !== "invalid" && status !== "expired" && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportAccessPage;
